import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/Button';
import CreateEventModal from '../../components/vendor/CreateEventModal';
import VendorEventDetailModal from '../../components/vendor/VendorEventDetailModal';
import { useUserProfile } from '../../hooks/useUserProfile';

const VendorEvents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get current user from AWS Cognito
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);
  const vendorId = userProfile?.currentVendorId;
  
  console.log('VendorEvents - currentUser:', currentUser);
  console.log('VendorEvents - userProfile:', userProfile);
  console.log('VendorEvents - vendorId:', vendorId);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      
      if (!vendorId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/events`);
        const eventsData = response.ok ? await response.json() : [];
        setEvents(eventsData);
        
        // Check if we need to open a specific event from navigation state
        if (location.state?.selectedEventId) {
          const eventToOpen = eventsData.find(e => e.id === location.state.selectedEventId);
          if (eventToOpen) {
            setSelectedEvent(eventToOpen);
            setShowEventDetail(true);
          }
          // Clear the state so it doesn't reopen on refresh
          navigate(location.pathname, { replace: true, state: {} });
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [vendorId, location.state]);

  const now = new Date();
  const endOfWeek = new Date(now); endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);

  const eventsThisWeek = events.filter(e => { const d = new Date(e.eventDate); return d >= now && d <= endOfWeek; }).length;
  const eventsThisMonth = events.filter(e => { const d = new Date(e.eventDate); return d >= now && d <= endOfMonth; }).length;
  const eventsNextMonth = events.filter(e => { const d = new Date(e.eventDate); return d > endOfMonth && d <= endOfNextMonth; }).length;

  // Filter events based on selected filter
  const filteredEvents = events.filter(event => {
    // Apply search filter
    if (searchQuery && !event.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !event.eventType?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Apply status filter
    if (filter === 'all') return true;
    if (filter === 'active') return event.status === 'active' || event.status === 'confirmed';
    if (filter === 'completed') return event.status === 'completed';
    if (filter === 'upcoming') return new Date(event.eventDate) > new Date();
    return true;
  }).sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));

  if (loading || profileLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading events...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '48px 24px 200px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '60px'
      }}>
        <h1 className="page-title" style={{ 
          fontSize: '56px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          Events
        </h1>
        
        {/* Stats Cards */}
        <div className="stats-grid" style={{ 
          position: 'absolute',
          bottom: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '20px',
          width: '100%',
          maxWidth: '1200px',
          padding: '0 24px'
        }}>
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '200px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {eventsThisWeek} <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'normal' }}>event{eventsThisWeek !== 1 ? 's' : ''} this week</span>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '200px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {eventsThisMonth} <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'normal' }}>event{eventsThisMonth !== 1 ? 's' : ''} this month</span>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '200px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {eventsNextMonth} <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'normal' }}>event{eventsNextMonth !== 1 ? 's' : ''} next month</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: '#F9FAFB'
            }}
          />
          
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              minWidth: '180px'
            }}
          >
            <option value="all">All Events</option>
            <option value="active">Active Events</option>
            <option value="completed">Completed Events</option>
            <option value="upcoming">Upcoming Events</option>
          </select>
          
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            Create Event
          </Button>
        </div>

        {/* Events List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredEvents.length === 0 ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '60px 40px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              {events.length === 0 ? (
                <>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                    You don't have any events yet
                  </h3>
                  <p style={{ color: '#6B7280', marginBottom: '24px' }}>
                    Create your first event to get started
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    style={{ padding: '12px 24px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                  >
                    Create Event
                  </button>
                </>
              ) : (
                <>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
                    No results found
                  </h3>
                  <p style={{ color: '#6B7280' }}>
                    Try adjusting your search or filters
                  </p>
                </>
              )}
            </div>
          ) : (
            filteredEvents.map(event => (
              <div 
                key={event.id} 
                onClick={() => {
                  setSelectedEvent(event);
                  setShowEventDetail(true);
                }}
                style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                cursor: 'pointer'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#E8F4E8',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '18px', color: '#1F2937', marginBottom: '4px' }}>
                    {event.name}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '14px' }}>
                    {event.eventType} - {event.status} - {new Date(event.eventDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <CreateEventModal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
      />
      
      {showEventDetail && selectedEvent && (
        <VendorEventDetailModal
          event={selectedEvent}
          onClose={() => {
            setShowEventDetail(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
};

export default VendorEvents;